import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button, Table, Pagination } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ user, data, currentTheme, onRemoveButton }) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(null);
  const [orderDirection, setOrderDirection] = useState('asc');

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  const handleSort = useCallback((column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0);
  }, [orderBy, orderDirection]);

  const getSortIcon = (column) => {
    if (orderBy !== column) return <FaSort className="ms-1" />;
    return orderDirection === 'asc' ?
      <FaSortUp className="ms-1" /> :
      <FaSortDown className="ms-1" />;
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      // Lógica de ordenação específica para cada coluna
      let valueA, valueB;

      switch (orderBy) {
        case 'serialnumber':
          valueA = a.serialnumber || '';
          valueB = b.serialnumber || '';
          break;
        case 'daystoactivate':
          valueA = parseInt(a.daystoactivate, 16);
          valueB = parseInt(b.daystoactivate, 16);
          break;
        case 'mode':
          valueA = a.mode || '';
          valueB = b.mode || '';
          break;
        case 'plan':
          valueA = a.plan || '';
          valueB = b.plan || '';
          break;
        case 'versaosw':
          valueA = a.versaosw || '';
          valueB = b.versaosw || '';
          break;
        case 'wf':
          valueA = a.wf === 'OK' ? 1 : 0;
          valueB = b.wf === 'OK' ? 1 : 0;
          break;
        case 'calph':
          valueA = a.calph === 'OK' ? 1 : 0;
          valueB = b.calph === 'OK' ? 1 : 0;
          break;
        case 'dcal':
          valueA = new Date(a.dcal || 0).getTime();
          valueB = new Date(b.dcal || 0).getTime();
          break;
        case 'isentkey':
          valueA = a.isentkey === true ? 1 : 0;
          valueB = b.isentkey === true ? 1 : 0;
          break;
        default:
          valueA = a[orderBy] || '';
          valueB = b[orderBy] || '';
      }

      if (valueA < valueB) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, orderBy, orderDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleRowClick = (id) => {
    history.push(`/equipments/${id}`);
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Equipamentos
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th className='text-center' onClick={() => handleSort('serialnumber')} style={{ cursor: 'pointer' }}>
                  SERIAL {getSortIcon('serialnumber')}
                </th>
                <th className='text-center' onClick={() => handleSort('daystoactivate')} style={{ cursor: 'pointer' }}>
                  ATIVAÇÃO {getSortIcon('daystoactivate')}
                </th>
                <th className='text-center' onClick={() => handleSort('mode')} style={{ cursor: 'pointer' }}>
                  MODO {getSortIcon('mode')}
                </th>
                <th className='text-center' onClick={() => handleSort('plan')} style={{ cursor: 'pointer' }}>
                  PLANO {getSortIcon('plan')}
                </th>
                <th className='text-center' onClick={() => handleSort('versaosw')} style={{ cursor: 'pointer' }}>
                  VERSÃO SOFTWARE {getSortIcon('versaosw')}
                </th>
                <th className='text-center' onClick={() => handleSort('wf')} style={{ cursor: 'pointer' }}>
                  WI-FI {getSortIcon('wf')}
                </th>
                <th className='text-center' onClick={() => handleSort('calph')} style={{ cursor: 'pointer' }}>
                  PH CALIBRADO {getSortIcon('calph')}
                </th>
                <th className='text-center' onClick={() => handleSort('dcal')} style={{ cursor: 'pointer' }}>
                  DATA CALIBRAÇÃO {getSortIcon('dcal')}
                </th>
                <th className='text-center' onClick={() => handleSort('isentkey')} style={{ cursor: 'pointer' }}>
                  ISENÇÃO DE TAXAS {getSortIcon('isentkey')}
                </th>
                <th className='text-center'>AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="clickable-row"
                  onClick={() => user?.permission?.equipments?.read && handleRowClick(item.serialnumber)}
                  style={{ cursor: user?.permission?.equipments?.read ? 'pointer' : 'default' }}
                >
                  <td className='text-center pt-2'>{item.serialnumber || '--'}</td>
                  <td className='text-center pt-2'>{parseInt(item.daystoactivate, 16)} dias</td>
                  <td className='text-center pt-2'>{item.mode === 'RENTAL' ? 'Locação' : 'Proprietário'}</td>
                  <td className='text-center pt-2'>{item.plan || 'NORMAL'}</td>
                  <td className='text-center pt-2'>{item.versaosw || '--'}</td>
                  <td className='text-center pt-2'>{item.wf === 'OK' ? 'Sim' : 'Não'}</td>
                  <td className='text-center pt-2'>{item.calph === 'OK' ? 'Sim' : 'Não'}</td>
                  <td className='text-center pt-2'>
                    {(() => {
                      var d = new Date(item.dcal);
                      let seconds = Math.floor((Date.now() - d.getTime()) / 1000);
                      return seconds > 31536000 ? <div style={{ color: "red" }}>{item.dcal || '--'}</div> : <div>{item.dcal || '--'}</div>
                    })()}
                  </td>
                  <td className='text-center pt-2'>{item?.isentkey === true ? 'Sim' : 'Não'}</td>
                  <td className='text-center'>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveButton(item._id);
                      }}
                      disabled={!user?.master}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"

            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"

            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
